<template>
  <div id="qr-code-page" class="qr-code-page">
    <div class="qr-code-container">
      <!-- Logo da empresa -->
      <div class="logo-container">
        <img class="logo" src="/Logo_Magnum.png" alt="Logo Magnum" />
      </div>

      <!-- Exibição do QR Code ou mensagem de carregamento -->
      <div class="qr-code-wrapper">
        <p v-if="qrCodeUrl" class="qr-code-instruction">
          Aponte seu celular para escanear o QR code.
        </p>
        <img
          v-if="qrCodeUrl && qrCodeUrl.startsWith('data:image')"
          :src="qrCodeUrl"
          alt="QR Code"
          class="qr-code-image"
        />
        <p v-else>Carregando QR Code...</p>
        <button @click="handleLogout" class="logout-button">
          Sair e trocar de conta
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuth } from "firebase/auth";
import { io } from "socket.io-client";
import { logout } from "@/utils/sessionUtils";
import { secureFetch } from "@/main";

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || "";
const socketUrl = process.env.VUE_APP_SOCKET_URL || apiBaseUrl;

// Namespacing por usuário
function storageKey(key, uid) { return `magleads:${uid}:${key}`; }
async function getUid() {
  const auth = getAuth();
  const user = auth.currentUser;
  return user?.uid || "anonymous";
}

export default {
  name: "QRCodePage",
  data() {
    return {
      qrCodeUrl: null,
      qrCheckInterval: null,
      socket: null,
      fallbackTimeout: null,
      qrSubscribed: false,
    };
  },
  beforeUnmount() {
    if (this.qrCheckInterval) clearInterval(this.qrCheckInterval);

    if (this.socket) {
      if (this.qrSubscribed) {
        this.socket.emit("qr:unsubscribe");
        this.qrSubscribed = false;
        console.log("👋 qr:unsubscribe enviado");
      }

      this.socket.off("new-qr");
      this.socket.off("client-ready");
      this.socket.off("whatsapp-authenticated");
      this.socket.off("connect");
      this.socket.off("connect_error");
      this.socket.disconnect();
      this.socket = null;
    }

    if (this.fallbackTimeout) clearTimeout(this.fallbackTimeout);
  },
  async mounted() {
    this.verificaSeJaEstaAutenticado();
  },
  methods: {
    async handleClientReady(payload) {
      if (payload.status === "connected") {
        console.log("✅ WebSocket/Polling: Cliente pronto. Redirecionando.");
        if (this.qrCheckInterval) {
          clearInterval(this.qrCheckInterval);
          this.qrCheckInterval = null;
        }

        const uid = await getUid();

        // 🔹 Flags "por usuário" (namespaced)
        localStorage.setItem(storageKey("qrCodeScanned", uid), "true");
        localStorage.setItem(storageKey("acessoComWhatsapp", uid), "true");
        const namespacedLastPage =
          localStorage.getItem(storageKey("currentPage", uid)) || "";

        // 🔹 Flags globais (usadas hoje pelo router e por telas antigas)
        localStorage.setItem("qrCodeScanned", "true");
        localStorage.setItem("acessoComWhatsapp", "true");

        const lastPage =
          namespacedLastPage ||
          localStorage.getItem("currentPage") ||
          "/pesquisa-cnpj";

        // Mantém currentPage global sincronizada
        localStorage.setItem("currentPage", lastPage);

        this.$router.replace(lastPage);
      }
    },

    async handleLogout() {
      if (this.qrCheckInterval) clearInterval(this.qrCheckInterval);
      if (this.socket) {
        if (this.qrSubscribed) {
          this.socket.emit("qr:unsubscribe");
          this.qrSubscribed = false;
        }
        this.socket.off("new-qr");
        this.socket.off("client-ready"); 
        this.socket.off("whatsapp-authenticated");
        this.socket.off("connect");
        this.socket.off("connect_error");
        this.socket.disconnect();
        this.socket = null;
      }
      if (this.fallbackTimeout) clearTimeout(this.fallbackTimeout);

      const uid = await getUid();
      ["qrCodeScanned", "acessoComWhatsapp", "currentPage"].forEach((k) =>
        localStorage.removeItem(storageKey(k, uid))
      );

      ["qrCodeScanned", "acessoComWhatsapp", "currentPage"].forEach((k) =>
        localStorage.removeItem(k)
      );

      await logout();
    },

    async verificaSeJaEstaAutenticado() {
      try {

        // Checa se o WhatsApp já está pronto
        const ws = await secureFetch(`${apiBaseUrl}/whatsapp-status`, {
          method: "GET",
        }).then((r) => r.json());

        if (ws.status === "ready") {
          const uid = await getUid();

          // Namespaced
          localStorage.setItem(storageKey("qrCodeScanned", uid), "true");
          localStorage.setItem(storageKey("acessoComWhatsapp", uid), "true");

          // Globais
          localStorage.setItem("qrCodeScanned", "true");
          localStorage.setItem("acessoComWhatsapp", "true");

          const namespacedLastPage =
            localStorage.getItem(storageKey("currentPage", uid)) || "";
          const lastPage =
            namespacedLastPage ||
            localStorage.getItem("currentPage") ||
            "/pesquisa-cnpj";

          localStorage.setItem("currentPage", lastPage);

          return this.$router.replace(lastPage);
        }

        if (ws.status === "restoring") {
          console.log("🔄 WhatsApp em restoring (BQ=true, Baileys subindo). Aguardando conexão...");
          this.authenticateWhatsApp(); // vai ouvir client-ready / whatsapp-authenticated
          this.pollQRCode();           // vai checar /whatsapp-status até virar ready
          return;
        }

        // Fluxo normal: socket + fetch inicial + fallback
        this.authenticateWhatsApp();
        this.fetchQRCode();
        this.pollQRCode();
      } catch (err) {
        console.error("❌ Erro ao verificar autenticação inicial:", err);
        this.authenticateWhatsApp();
        this.fetchQRCode();
        this.pollQRCode();
      }
    },

    async getToken() {
      // 1) Tenta token salvo via SSO (mag-auth.html)
      const ssoToken = localStorage.getItem("userToken");
      if (ssoToken) {
        return ssoToken;
      }

      // 2) Fallback: token do Firebase (login local / dev)
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        return await user.getIdToken(true);
      }

      // 3) Nenhum token → força login (REMOVIDO ALERT)
      console.error("Usuário não autenticado. Redirecionando para login.");
      this.$router.push("/");
      return null;
    },

    // 1) Autenticar via socket e ouvir QR/eventos por usuário
    async authenticateWhatsApp() {
      try {
        const token = await this.getToken();
        if (!token) {
          console.error("🔴 Nenhum token disponível.");
          return;
        }

        // Limpa socket antigo, se existir
        if (this.socket) {
          this.socket.off("new-qr");
          this.socket.off("client-ready"); 
          this.socket.off("whatsapp-authenticated");
          this.socket.off("connect");
          this.socket.off("connect_error");
          this.socket.disconnect();
          this.socket = null;
        }

        this.socket = io(socketUrl, {
          path: "/socket.io",
          transports: ["websocket", "polling"],
          auth: { token },
        });

        // ADIÇÃO CRÍTICA: Ouve o evento de conexão bem-sucedida do WhatsApp
        this.socket.on("client-ready", this.handleClientReady);

        this.socket.on("whatsapp-authenticated", () => {
          this.handleClientReady({ status: "connected" });
        });

        let fallbackAtivado = false;
        this.fallbackTimeout = null;

        this.socket.on("connect", async () => {
          console.log("🟢 Socket conectado:", this.socket.id);

          if (!this.qrSubscribed) {
            this.socket.emit("qr:subscribe");
            this.qrSubscribed = true;
            console.log("👀 qr:subscribe enviado");
          }

          // Se nenhum QR chegar em 5s, ativa fallback
          this.fallbackTimeout = setTimeout(() => {
            if (!this.qrCodeUrl && !fallbackAtivado) {
              console.warn("⏱️ Nenhum QR via socket em 5s. Iniciando fallback com polling...");
              fallbackAtivado = true;
              this.pollQRCode();
            }
          }, 5000);
        });

        this.socket.on("new-qr", (data) => {
          console.log("📸 Novo QR recebido via socket.");
          if (this.fallbackTimeout) clearTimeout(this.fallbackTimeout);
          // só aceita QR válido (data:image)
          if (!data?.qrCodeUrl?.startsWith("data:image")) {
            console.warn("QR recebido sem data:image — ignorado.");
            return;
          }
          this.qrCodeUrl = data.qrCodeUrl;
          console.log("✅ QR Code carregado com sucesso!");
        });

        this.socket.on("connect_error", (err) => {
          console.error("🔴 Erro de conexão socket:", err.message);
          if (!fallbackAtivado) {
            fallbackAtivado = true;
            this.pollQRCode();
          }
        });
      } catch (error) {
        console.error("❌ Erro ao autenticar WhatsApp:", error);
        this.pollQRCode();
      }
    },

    // Busca inicial do QR (caso já exista no backend)
async fetchQRCode() {
  try {
    console.log("🔄 Buscando QR Code...");
    const token = await this.getToken();
    if (!token) {
      console.error("🔴 Nenhum token disponível. Usuário não autenticado.");
      return;
    }

    const response = await secureFetch(`${apiBaseUrl}/qr-code`, {
      method: "GET",
    });

    if (response.status === 401) {
      console.error("🔴 Erro 401: Não autorizado.");
      return;
    }

    // ✅ 202 = backend ainda gerando / aguardando QR (não tratar como erro)
    if (response.status === 202) {
      console.log("⏳ Backend ainda emitindo QR (202). Tentando novamente...");
      setTimeout(() => this.fetchQRCode(), 2000);
      return;
    }

    if (response.status === 404) {
      console.log("⚠️ Nenhum QR Code encontrado. Tentando novamente em breve...");
      setTimeout(() => this.fetchQRCode(), 3000);
      return;
    }

    if (!response.ok) {
      throw new Error(`Erro ao obter QR Code. Status: ${response.status}`);
    }

    const data = await response.json();

    // ✅ Se o backend informar pronto/escaneado, redireciona pela rotina unificada
    if (data?.scanned === true || data?.status === "ready") {
      // se estiver com polling ligado, para aqui
      if (this.qrCheckInterval) clearInterval(this.qrCheckInterval);
      this.handleClientReady({ status: "connected" });
      return;
    }

    // ✅ aceita apenas data:image
    if (data?.qrCodeUrl?.startsWith("data:image")) {
      this.qrCodeUrl = data.qrCodeUrl;
      await this.$nextTick();
      console.log("✅ QR Code carregado com sucesso!");
      return;
    }

    // Se veio JSON sem QR válido, apenas tenta novamente (sem log de erro)
    console.log("⌛ QR ainda não disponível (sem data:image). Aguardando...");
    setTimeout(() => this.fetchQRCode(), 2000);
  } catch (error) {
    console.error("❌ Erro ao buscar QR Code:", error);
    setTimeout(() => this.fetchQRCode(), 3000);
  }
},

    // 3)Fallback com polling: checa pronto em /whatsapp-status e QR atual em /qr-code
    async pollQRCode() {
      if (this.qrCheckInterval) clearInterval(this.qrCheckInterval);

      let previousQr = "";

      this.qrCheckInterval = setInterval(async () => {
        try {
          const token = await this.getToken();
          if (!token) {
            console.log("🔴 Nenhum token encontrado. Redirecionando para login.");
            clearInterval(this.qrCheckInterval);
            this.$router.push("/");
            return;
          }

          // 1) se já está pronto, sai
          const wsResp = await secureFetch(`${apiBaseUrl}/whatsapp-status`, { method: "GET" });
          if (wsResp.ok) {
            const ws = await wsResp.json();
            if (ws.status === "ready") {
              clearInterval(this.qrCheckInterval);
              this.handleClientReady({ status: "connected" });
              return;
            }
          }

          // 2) tenta pegar QR atual
          const resp = await secureFetch(`${apiBaseUrl}/qr-code`, { method: "GET" });

          // ✅ 202: backend ainda gerando QR — segue o polling sem warning
          if (resp.status === 202) {
            return;
          }

          if (resp.ok) {
            const data = await resp.json();

            // ✅ se backend disser pronto/escaneado, redireciona
            if (data?.scanned === true || data?.status === "ready") {
              clearInterval(this.qrCheckInterval);
              this.handleClientReady({ status: "connected" });
              return;
            }

            const qr = data?.qrCodeUrl || "";
            if (qr && qr !== previousQr && qr.startsWith("data:image")) {
              this.qrCodeUrl = qr;
              previousQr = qr;
              console.log("✅ QR Code atualizado via fallback.");
            }

            return;
          }

          // 404 = ainda não existe QR para esse user (normal)
          if (resp.status !== 404) {
            console.warn("QR polling: backend sem QR ou erro transitório. Status:", resp.status);
          }
        } catch (e) {
          console.error("QR polling error:", e);
        }
      }, 3000);
    },
  },
};
</script>

<style src="@/assets/styleQRCode.css"></style>